import { useTranslation } from "react-i18next";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { recordPropertySearched } from "~/api/audit/record";
import { type AddressSearchAutocompleteItem } from "~/api/manual/address-search";
import { AddressSearch } from "~/components/AddressSearch/AddressSearch";
import { SearchPageLayout } from "~/components/layout/SearchPageLayout";
import { useTracking } from "~/hooks/useTracking";
import { useNavigateToPropertyCheck } from "../-hooks/useNavigateToPropertyCheck";

export const Route = createFileRoute("/(restricted)/ejendomstjek/(search)")({
  component: Home,
  validateSearch: z.object({
    q: z.string().optional(),
  }),
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("property-check");

    const title = i18n.t("property-check:meta.title");

    return { meta: { title } };
  },
  head: (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
});

function Home() {
  const navigate = Route.useNavigate();
  const { q = "" } = Route.useSearch(); // Default to empty string, for easy compare.
  const { t } = useTranslation();
  const navigateToPropertyCheck = useNavigateToPropertyCheck();
  const { trackEvent } = useTracking();

  const handleValueSelect = async (
    selected: AddressSearchAutocompleteItem | null,
  ) => {
    if (!selected) return;

    const { id, access_address_id, address } = selected;
    trackEvent("address_search_result_selected", {
      area: "Ejendomstjek",
      address,
      access_address_id,
      place_id: id !== access_address_id ? id : null,
    });
    recordPropertySearched(access_address_id);

    await navigateToPropertyCheck(selected);
  };

  const handleNoResults = (inputVal: string) => {
    trackEvent("address_search_no_result", {
      area: "Ejendomstjek",
      search: inputVal,
    });
  };

  // Update the search query in the URL
  const handleSearchInputChange = async (value: string) => {
    // NOTE: Only trigger if we actually have some new to route to.
    if (q !== value) {
      await navigate({
        to: "/ejendomstjek",
        search: value ? { q: value } : undefined,
        replace: true,
        resetScroll: false,
        viewTransition: false,
      });
    }
  };

  return (
    <SearchPageLayout
      headline={t("search.headline")}
      caption={t("search.caption")}
      mobileDisclaimer={t("search.mobile_disclaimer")}
    >
      <AddressSearch
        placeholder={t("search.input.placeholder")}
        initialInputValue={q}
        onValueSelect={handleValueSelect}
        onNoResults={handleNoResults}
        onInputChange={handleSearchInputChange}
        size="hero"
      />
    </SearchPageLayout>
  );
}
